/**
 * Clinical Results Component
 * Displays clinical study results with circular progress indicators
 */

'use client';

import styled from '@emotion/styled';
import { Accordion, useTheme } from '@template-project/afrodita-components';
import { ProductClinicalResult } from '@/domain/entities/product.entity';

export interface ClinicalResultsProps {
  title?: string;
  clinicalResults: ProductClinicalResult[];
  disclaimer?: string;
  defaultOpened?: boolean;
}

const Container = styled.section`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: ${({ theme }) => `${theme.space['2xl']} ${theme.space.md}`};
`;

const AccordionWrapper = styled.div`
  width: 100%;
`;

const MainWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space.xl};
`;

const DataWrapper = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: ${({ theme }) => theme.space.xl};
  padding: ${({ theme }) => `${theme.space.md} 0`};

  @media (max-width: ${({ theme }) => theme.mediaQueries.md}) {
    grid-template-columns: 1fr;
    gap: ${({ theme }) => theme.space['2xl']};
  }
`;

const ResultWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: ${({ theme }) => theme.space.md};
`;

const Graph = styled.div`
  position: relative;
  width: 120px;
  height: 120px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ValueGraph = styled.span`
  position: absolute;
  color: ${({ theme }) => theme.colors.semantic.positive.color};
  font-size: 2rem;
  font-weight: ${({ theme }) => theme.typography.config.weight.bold};
  z-index: 1;
`;

const CircleProgress = styled.svg<{ progress: number }>`
  position: relative;
  --progress: ${({ progress }) => progress};
  --size: 100%;
  --stroke-width: 4px;
  --half-size: calc(var(--size) / 2);
  --radius: calc((var(--size) - var(--stroke-width)) / 2);
  --circumference: calc(var(--radius) * 3.14159 * 2);
  --dash: calc((var(--progress) * var(--circumference)) / 100);

  circle {
    cx: var(--half-size);
    cy: var(--half-size);
    r: var(--radius);
    stroke-width: var(--stroke-width);
    fill: none;
    stroke-linecap: round;
  }

  circle.bg {
    stroke: ${({ theme }) => theme.colors.background.secondary};
  }

  circle.fg {
    transform: rotate(-90deg);
    transform-origin: var(--half-size) var(--half-size);
    stroke-dasharray: var(--dash) calc(var(--circumference) - var(--dash));
    stroke: ${({ theme }) => theme.colors.semantic.positive.color};
    transition: stroke-dasharray 1s ease-in-out;
  }
`;

const Description = styled.p`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.fontSizes[2]}px;
  line-height: 1.6;
  max-width: 300px;
`;

const Disclaimer = styled.p`
  color: ${({ theme }) => theme.colors.shades[250].color};
  font-size: ${({ theme }) => theme.fontSizes[1]}px;
  line-height: 1.5;
  text-align: center;
  font-style: italic;
`;

const Title = styled.h3`
  font-size: ${({ theme }) => theme.typography.variants.heading4.fontSize}px;
  font-weight: ${({ theme }) => theme.typography.config.weight.demi};
  margin: 0;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const PlusIcon = styled.span`
  display: inline-block;
  width: 20px;
  height: 20px;
  position: relative;
  
  &::before,
  &::after {
    content: '';
    position: absolute;
    background-color: currentColor;
  }
  
  &::before {
    width: 20px;
    height: 2px;
    top: 9px;
    left: 0;
  }
  
  &::after {
    width: 2px;
    height: 20px;
    top: 0;
    left: 9px;
  }
`;

const MinusIcon = styled.span`
  display: inline-block;
  width: 20px;
  height: 2px;
  background-color: currentColor;
`;

export function ClinicalResults({
  title = 'Clinical Results',
  clinicalResults,
  disclaimer,
  defaultOpened = true,
}: ClinicalResultsProps) {
  const theme = useTheme();

  if (!clinicalResults || clinicalResults.length === 0) {
    return null;
  }

  return (
    <Container>
      <AccordionWrapper>
        <Accordion
          closeIcon={MinusIcon}
          content={
            <MainWrapper>
              <DataWrapper>
                {clinicalResults.map(({ id, value, isPercent, description }) => (
                  <ResultWrapper key={id}>
                    <Graph>
                      <ValueGraph>
                        {value}
                        {isPercent && '%'}
                      </ValueGraph>
                      <CircleProgress
                        height="100%"
                        progress={isPercent ? value : 100}
                        viewBox="0 0 127 127"
                        width="100%"
                      >
                        <circle className="bg" />
                        <circle className="fg" />
                      </CircleProgress>
                    </Graph>
                    <Description>{description}</Description>
                  </ResultWrapper>
                ))}
              </DataWrapper>
              {disclaimer && <Disclaimer>{disclaimer}</Disclaimer>}
            </MainWrapper>
          }
          defaultOpen={defaultOpened}
          header={<Title>{title}</Title>}
          openIcon={PlusIcon}
          variant="simple"
        />
      </AccordionWrapper>
    </Container>
  );
}

